import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from './Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import { useTenant } from '../../core/contexts/TenantContext';
import { StatusChip } from './StatusChip';
import type {
  ReportPriority,
  ReportStatus,
  ReportTemplate,
} from '../../core/types/data-models';

interface NewReportTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * LOB id the new template attaches to. Driven by the active LOB
   * selected in the sidebar — no UI picker; the dialog refuses to
   * submit when this is null.
   */
  defaultLineOfBizId?: number | null;
  /** Called with the newly created template after a successful POST. */
  onCreated?: (template: ReportTemplate) => void;
}

const priorities: ReportPriority[] = ['LOW', 'MEDIUM', 'HIGH'];
const statuses: ReportStatus[] = ['ACTIVE', 'DRAFT', 'DISABLED'];

function clampPeriod(raw: string): number | null {
  if (raw === '') return null;
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return null;
  return Math.min(31, Math.max(1, n));
}

/**
 * Lightweight create dialog used by the sidebar's "+ New report template"
 * entry under the Report Template dropdown. Mirrors the fields rendered
 * by `ReportTemplatesPage`'s built-in CrudPage create form so the two
 * code paths produce identical rows. Schedule defaults to DAILY on
 * weekdays — matching the existing CrudPage default.
 */
export function NewReportTemplateDialog({
  open,
  onClose,
  defaultLineOfBizId = null,
  onCreated,
}: NewReportTemplateDialogProps) {
  const { activeTenantId } = useTenant();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [relevantPeriod, setRelevantPeriod] = useState<number | null>(null);
  const [defaultPriority, setDefaultPriority] = useState<ReportPriority>('MEDIUM');
  // New templates start as DRAFT — they're not ready for scheduled runs
  // until the user finishes wiring prompts/sets to them.
  const [status, setStatus] = useState<ReportStatus>('DRAFT');
  const [catalogId, setCatalogId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Optional commerce catalog this template targets. Scoped to the active tenant.
  const catalogs = useQuery({
    queryKey: ['commerce-catalogs', 'select', activeTenantId],
    queryFn: () =>
      resources.commerceCatalogs.list({
        pageSize: 200,
        sort: 'name',
        ...(activeTenantId != null ? { filter: { tenantId: activeTenantId } } : {}),
      }),
  });

  const reset = () => {
    setName('');
    setDescription('');
    setRelevantPeriod(null);
    setDefaultPriority('MEDIUM');
    setStatus('DRAFT');
    setCatalogId(null);
    setError(null);
  };

  const create = useMutation({
    mutationFn: (payload: Partial<ReportTemplate>) => resources.reportTemplates.create(payload),
    onSuccess: (tpl) => {
      // Invalidate every reader of the templates catalog — the list page,
      // the sidebar dropdown, and any other consumer using the
      // `['report-templates']` prefix.
      queryClient.invalidateQueries({ queryKey: ['report-templates'] });
      onCreated?.(tpl);
      reset();
      onClose();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (defaultLineOfBizId == null) {
      setError('Select a line of business in the sidebar first.');
      return;
    }
    if (relevantPeriod == null) {
      setError('Relevant period is required (1 to 31 days)');
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        lineOfBizId: defaultLineOfBizId,
        catalogId,
        relevantPeriod,
        defaultPriority,
        status,
        // Same DAILY-weekday default as the existing CrudPage form.
        schedule: {
          runType: 'DAILY',
          runWeekDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
        },
        ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
      } as Partial<ReportTemplate>);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <FormDialog
      open={open}
      title="New report template"
      submitLabel="Create"
      loading={create.isPending}
      onClose={() => {
        reset();
        onClose();
      }}
      onSubmit={handleSubmit}
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Stack spacing={2}>
        <TextField
          label="Name"
          required
          autoFocus
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Description"
          multiline
          minRows={2}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField
          label="Relevant period (days)"
          type="number"
          required
          inputProps={{ min: 1, max: 31, step: 1 }}
          helperText="Required. 1 to 31 days. Determines how far back the report's data window reaches."
          value={relevantPeriod ?? ''}
          onChange={(e) => setRelevantPeriod(clampPeriod(e.target.value))}
        />
        <FormControl fullWidth>
          <InputLabel id="new-rt-priority-label">Default priority</InputLabel>
          <Select
            labelId="new-rt-priority-label"
            label="Default priority"
            value={defaultPriority}
            renderValue={(v) => <StatusChip value={v as string} />}
            onChange={(e) => setDefaultPriority(e.target.value as ReportPriority)}
          >
            {priorities.map((p) => (
              <MenuItem key={p} value={p}>
                <StatusChip value={p} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="new-rt-status-label">Status</InputLabel>
          <Select
            labelId="new-rt-status-label"
            label="Status"
            value={status}
            renderValue={(v) => <StatusChip value={v as string} />}
            onChange={(e) => setStatus(e.target.value as ReportStatus)}
          >
            {statuses.map((s) => (
              <MenuItem key={s} value={s}>
                <StatusChip value={s} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="new-rt-catalog-label">Commerce catalog</InputLabel>
          <Select
            labelId="new-rt-catalog-label"
            label="Commerce catalog"
            value={catalogId ?? ''}
            onChange={(e) =>
              setCatalogId(e.target.value === '' ? null : Number(e.target.value))
            }
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {(catalogs.data?.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </FormDialog>
  );
}
